import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Breadcrumbs,
  Link,
  Chip,
  IconButton,
} from "@mui/material";
import { Visibility, OpenInNew } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { makeStyles } from "@mui/styles";
import dayjs from "dayjs";
import Spinner from "../../components/Spinner";
import CommonPagination from "../../components/Table/CommonPagination";
import { getUserDetail, getUserVotes } from "../../http/auth";
import routePaths from "../../router/route-paths";
import UserDevicesSection from "./UserDevicesSection";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
    maxHeight: "calc(100vh - 400px)",
    minHeight: "calc(100vh - 400px)",
    boxShadow: "none !important",
  },
  table: {
    width: "100%",
    tableLayout: "fixed",
    border: "1px solid rgba(224, 224, 224, 1)",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
}));

// Vote type badge colors
const getVoteTypeConfig = (voteType) => {
  const configs = {
    PROMPT_POST: { bg: "#e3f2fd", color: "#1976d2", label: "Prompt" },
    CATEGORY_POST: { bg: "#f3e5f5", color: "#7b1fa2", label: "Category" },
    KARAOKE_POST: { bg: "#fce4ec", color: "#c2185b", label: "Karaoke" },
    DANCE_POST: { bg: "#ccfbf1", color: "#0f766e", label: "Dance" },
    CONTEST_POST: { bg: "#fff8e1", color: "#ff8f00", label: "Contest" },
    LIVE: { bg: "#e8f5e9", color: "#2e7d32", label: "Live" },
  };
  return configs[voteType] || { bg: "#f5f5f5", color: "#757575", label: voteType || "Unknown" };
};

// Outcome badge colors
const getOutcomeConfig = (outcome) => {
  const configs = {
    WIN: { bg: "#e8f5e9", color: "#2e7d32", label: "Win" },
    LOSE: { bg: "#ffebee", color: "#c62828", label: "Lose" },
    DRAW: { bg: "#eeeeee", color: "#616161", label: "Draw" },
    IN_PROGRESS: { bg: "#fff8e1", color: "#f9a825", label: "In Progress" },
  };
  return configs[outcome] || { bg: "#f5f5f5", color: "#757575", label: outcome || "Unknown" };
};

const ViewUser = () => {
  const classes = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [userDetail, setUserDetail] = useState(null);
  const [votes, setVotes] = useState([]);
  const [error, setError] = useState(null);

  // Pagination state (API uses 1-based page)
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch user detail
  useEffect(() => {
    const fetchUserDetail = async () => {
      try {
        const response = await getUserDetail(id);
        setUserDetail(response.data?.data || response.data);
      } catch (err) {
        console.error("Failed to fetch user detail", err);
        toast.error("Failed to load user details");
      }
    };

    if (id) {
      fetchUserDetail();
    }
  }, [id]);

  // Fetch votes
  useEffect(() => {
    const fetchVotes = async () => {
      setLoading(true);
      try {
        const response = await getUserVotes(id, page, limit);
        if (response.success && response.data) {
          setVotes(response.data.votes || []);
          setTotalCount(response.data.totalCount || 0);
        } else {
          setVotes([]);
          setTotalCount(0);
        }
        setError(null);
      } catch (err) {
        console.error("Failed to fetch user votes", err);
        setError(err.message || "Failed to load votes");
        setVotes([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchVotes();
    }
  }, [id, page, limit]);

  const handlePageChange = (event, newPage) => {
    // CommonPagination uses 0-based page, API uses 1-based
    setPage(newPage + 1);
  };

  // Common cell style for headers
  const headerCellStyle = {
    fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "15px" },
    color: "#000",
    fontWeight: 600,
    padding: { xs: "10px 8px", sm: "12px 10px", md: "14px 12px" },
    backgroundColor: "#e0e0e0",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  // Common cell style for body
  const bodyCellStyle = {
    fontSize: { xs: "11px", sm: "12px", md: "13px", lg: "14px", xl: "14px" },
    color: "#333",
    padding: { xs: "8px 8px", sm: "10px", md: "12px" },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const renderVideoLink = (link, index) => {
    if (!link) {
      return (
        <Typography
          component="span"
          sx={{
            fontSize: "12px",
            color: "#999",
            fontStyle: "italic",
          }}
        >
          Unavailable
        </Typography>
      );
    }
    return (
      <IconButton
        size="small"
        onClick={() => window.open(link, "_blank")}
        title={`Open Video ${index + 1}`}
        sx={{ color: "#1976d2" }}
      >
        <OpenInNew fontSize="small" />
      </IconButton>
    );
  };

  return (
    <Box>
      <ToastContainer />
      {loading && <Spinner />}

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ marginBottom: "16px" }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate(routePaths.MANAGE_USERS)}
        >
          Manage Users
        </Link>
        <Typography color="text.primary">
          {userDetail?.username || "View User"}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box className={classes.header}>
        <Typography
          sx={{
            fontSize: { xs: "15px", sm: "16px", md: "18px", lg: "20px", xl: "24px" },
            marginTop: "4px",
          }}
          variant="h5"
        >
          User Vote Analytics
        </Typography>
      </Box>

      {/* User Summary */}
      {userDetail && (
        <Box
          sx={{
            padding: "16px",
            marginBottom: "20px",
            borderRadius: "8px",
            backgroundColor: "#f5f5f5",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              marginBottom: "12px",
              color: "#142C5C",
            }}
          >
            User Information
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
              gap: "12px",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>Username</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                {userDetail.username || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>Name</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                {`${userDetail.firstName || ""} ${userDetail.lastName || ""}`.trim() || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>Email</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                {userDetail.email || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>Total Votes Received</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                {totalCount}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Anti-cheat: installs this account has used and other accounts on them */}
      {id && <UserDevicesSection userId={id} />}

      {/* Votes Section */}
      <Box
        sx={{
          padding: "15px",
          borderRadius: "6px",
          boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "14px", sm: "15px", md: "16px" },
            marginBottom: "16px",
            color: "#142C5C",
          }}
        >
          Votes Cast For This User
        </Typography>

        {error && (
          <Typography color="error" sx={{ marginBottom: "16px" }}>
            {error}
          </Typography>
        )}

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.stickyHeader}>
                <TableCell sx={{ ...headerCellStyle, width: "15%" }}>
                  Date/Time
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                  Vote Type
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "22%" }}>
                  Players
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                  Outcome
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                  Vote Weight
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                  Retroactive
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "19%" }}>
                  Videos
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {votes.length > 0 ? (
                votes.map((vote) => {
                  const voteTypeConfig = getVoteTypeConfig(vote.voteType);
                  const outcomeConfig = getOutcomeConfig(vote.outcome);
                  const players = vote.match?.playerUsernames || [];
                  const videoLinks = vote.match?.videoLinks || [];

                  return (
                    <TableRow key={vote._id}>
                      <TableCell sx={bodyCellStyle}>
                        {dayjs(vote.timestamp).format("MMM D, YYYY h:mm A")}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        <Chip
                          label={voteTypeConfig.label}
                          size="small"
                          sx={{
                            backgroundColor: voteTypeConfig.bg,
                            color: voteTypeConfig.color,
                            fontWeight: 500,
                            fontSize: "11px",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellStyle,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {players.length >= 2
                          ? `${players[0]} vs ${players[1]}`
                          : players.join(", ") || "-"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        <Chip
                          label={outcomeConfig.label}
                          size="small"
                          sx={{
                            backgroundColor: outcomeConfig.bg,
                            color: outcomeConfig.color,
                            fontWeight: 500,
                            fontSize: "11px",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellStyle, textAlign: "center" }}>
                        {vote.votingPowerWeight || 1}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellStyle, textAlign: "center" }}>
                        {vote.isRetroactive ? "Yes" : "No"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {videoLinks.length > 0 ? (
                            videoLinks.map((link, idx) => (
                              <Box key={idx} sx={{ display: "flex", alignItems: "center" }}>
                                <Typography component="span" sx={{ fontSize: "11px", mr: 0.5 }}>
                                  V{idx + 1}:
                                </Typography>
                                {renderVideoLink(link, idx)}
                              </Box>
                            ))
                          ) : (
                            <Typography sx={{ fontSize: "12px", color: "#999" }}>
                              -
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      textAlign: "center",
                      height: "200px",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      color: "#666",
                    }}
                  >
                    {loading ? "Loading votes..." : "No votes found for this user"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalCount > 0 && (
          <Box sx={{ marginTop: "16px" }}>
            <CommonPagination
              rowsPerPage={limit}
              count={totalCount}
              page={page - 1}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ViewUser;
